/**
 * Survey Modal Store
 * Global state for controlling the survey modal from anywhere in the app
 */

import { create } from 'zustand'
import type { SurveyTrigger } from '@/services/surveys/api'

interface SurveyModalState {
  isOpen: boolean
  trigger: SurveyTrigger | undefined
  metadata:
    | {
        orderId?: string
        invitationId?: string
        triggerContext?: string
      }
    | undefined
  openSurveyModal: (
    trigger: SurveyTrigger,
    metadata?: SurveyModalState['metadata']
  ) => void
  closeSurveyModal: () => void
}

const DISMISS_KEY = 'dday-survey-dismissed'

// Stores a future Unix ms timestamp — when the survey is next allowed to show.
// Cooldown is baked in at write time so the read check is just Date.now() > resumeAt.
function getResumeAt(): number | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(DISMISS_KEY)
  if (!stored) return null
  const parsed = parseInt(stored, 10)
  return isNaN(parsed) ? null : parsed
}

function setResumeAt(resumeAt: number): void {
  localStorage.setItem(DISMISS_KEY, resumeAt.toString())
}

export function shouldShowSurvey(): boolean {
  const resumeAt = getResumeAt()
  return resumeAt === null || Date.now() > resumeAt
}

export function recordSurveyDismissal(cooldownDays: number = 3): void {
  setResumeAt(Date.now() + cooldownDays * 24 * 60 * 60 * 1000)
}

export const useSurveyModalStore = create<SurveyModalState>((set) => ({
  isOpen: false,
  trigger: undefined,
  metadata: undefined,
  openSurveyModal: (trigger, metadata) =>
    set({ isOpen: true, trigger, metadata }),
  closeSurveyModal: () =>
    set({ isOpen: false, trigger: undefined, metadata: undefined }),
}))
